import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  Loader2,
  Megaphone,
  MessageSquare,
  Search,
  ShieldAlert,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { useAccountDialogs, useAccounts, type AccountDialog } from "@/api/accounts";
import { useImportAccountDialogs } from "@/api/chats";
import { ApiError } from "@/api/client";
import { DragScroll } from "@/components/DragScroll";
import { Button } from "@/design";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ChatAccountImportSheet({ open, onClose }: Props) {
  const { data: accounts } = useAccounts();
  const firstAccountId = accounts?.[0]?.id ?? null;
  const [accountId, setAccountId] = useState<number | null>(firstAccountId);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const activeAccountId = accountId ?? firstAccountId;
  const { data: dialogs, isLoading, isError } = useAccountDialogs(activeAccountId);
  const importMut = useImportAccountDialogs();

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (dialogs ?? [])
      .filter((d) => !d.is_existing)
      .filter((d) => (q ? `${d.title} ${d.chat_ref}`.toLowerCase().includes(q) : true));
  }, [dialogs, query]);

  const selectedItems = useMemo(
    () => (dialogs ?? []).filter((d) => selected.has(d.peer_id) && !d.is_existing),
    [dialogs, selected],
  );

  const toggle = (peerId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(peerId)) next.delete(peerId);
      else next.add(peerId);
      return next;
    });
    haptic.select();
  };

  const handleImport = async () => {
    if (selectedItems.length === 0) return;
    haptic.impact("medium");
    try {
      const res = await importMut.mutateAsync({
        items: selectedItems.map((d) => ({
          peer_id: d.peer_id,
          peer_type: d.peer_type,
          title: d.title,
          chat_ref: d.chat_ref,
        })),
      });
      haptic.notify("success");
      toast.success(`Добавлено ${res.added}`);
      setSelected(new Set());
      onClose();
    } catch (err) {
      haptic.notify("error");
      if (err instanceof ApiError && err.status === 412) {
        toast.error(err.message);
      } else if (err instanceof ApiError && err.status === 429) {
        toast.error("Telegram попросил подождать. Попробуйте позже.");
      } else {
        toast.error((err as Error)?.message || "Не удалось импортировать");
      }
    }
  };

  const handleClose = () => {
    if (importMut.isPending) return;
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
          />
          <motion.div
            className={cn(
              "fixed inset-x-0 bottom-0 top-8 z-50",
              "glass-strong rounded-t-[28px] overflow-hidden",
              "border-t border-white/[0.08] flex flex-col",
            )}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-3 shrink-0 border-b border-white/[0.04]">
              <button
                onClick={handleClose}
                className="size-9 grid place-items-center rounded-full bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                aria-label="Закрыть"
              >
                <ArrowLeft className="size-4" />
              </button>
              <div>
                <div className="text-base font-semibold text-center">Импорт из аккаунта</div>
                <div className="text-[11px] text-text-muted text-center">Группы и каналы, где аккаунт уже состоит</div>
              </div>
              <Button
                size="sm"
                onClick={handleImport}
                disabled={selectedItems.length === 0 || importMut.isPending}
                loading={importMut.isPending}
              >
                Добавить {selectedItems.length > 0 && `(${selectedItems.length})`}
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {(accounts ?? []).length === 0 ? (
                <EmptyNotice text="Сначала подключите Telegram-аккаунт" />
              ) : (
                <>
                  {(accounts ?? []).length > 1 && (
                    <DragScroll className="flex gap-2">
                      {(accounts ?? []).map((a) => (
                        <button
                          key={a.id}
                          onClick={() => {
                            setAccountId(a.id);
                            setSelected(new Set());
                            haptic.select();
                          }}
                          className={cn(
                            "shrink-0 h-9 px-3 rounded-full text-[12px] font-medium border transition-colors",
                            activeAccountId === a.id
                              ? "bg-accent text-white border-accent"
                              : "bg-white/[0.04] text-text-muted border-white/[0.06]",
                          )}
                        >
                          {a.name}
                        </button>
                      ))}
                    </DragScroll>
                  )}

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-dim" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Поиск по чатам"
                      className="w-full h-11 rounded-2xl bg-surface border border-white/[0.06] pl-10 pr-4 text-sm outline-none focus:border-accent/40"
                    />
                  </div>

                  {isLoading ? (
                    <LoadingState />
                  ) : isError ? (
                    <EmptyNotice text="Не удалось загрузить диалоги. Попробуйте позже." />
                  ) : visible.length === 0 ? (
                    <EmptyNotice text="Новых групп и каналов не найдено" />
                  ) : (
                    <div className="space-y-2">
                      {visible.map((d) => (
                        <DialogRow
                          key={d.peer_id}
                          dialog={d}
                          selected={selected.has(d.peer_id)}
                          onClick={() => toggle(d.peer_id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function DialogRow({
  dialog,
  selected,
  onClick,
}: {
  dialog: AccountDialog;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full rounded-2xl p-3 flex items-center gap-3 text-left border transition-colors",
        selected
          ? "bg-accent/[0.08] border-accent/35"
          : "bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]",
      )}
    >
      <div
        className={cn(
          "size-10 rounded-xl grid place-items-center shrink-0",
          dialog.peer_type === "channel" ? "bg-accent-soft text-accent" : "bg-white/[0.06] text-text-muted",
        )}
      >
        {dialog.peer_type === "channel" ? <Megaphone className="size-4" /> : <MessageSquare className="size-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium truncate">{dialog.title}</div>
        <div className="text-[11px] text-text-muted truncate">{dialog.chat_ref}</div>
        {!dialog.can_write && (
          <div className="text-[10px] text-warning flex items-center gap-1 mt-1">
            <ShieldAlert className="size-2.5" /> возможно, нет права писать
          </div>
        )}
      </div>
      <div
        className={cn(
          "size-6 rounded-md border grid place-items-center shrink-0 transition-colors",
          selected ? "bg-accent border-accent" : "border-white/[0.15] bg-transparent",
        )}
      >
        {selected && <Check className="size-3.5 text-white" />}
      </div>
    </button>
  );
}

function LoadingState() {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 flex flex-col items-center gap-2 text-text-muted">
      <Loader2 className="size-5 animate-spin" />
      <div className="text-sm">Загружаем диалоги аккаунта</div>
    </div>
  );
}

function EmptyNotice({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/[0.08] bg-white/[0.01] p-8 text-center text-sm text-text-muted">
      {text}
    </div>
  );
}
